import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setAccess(r.data.access);
    } catch {
      setUser(null);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from emergent OAuth callback, skip /me — AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginPassword = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // Seed auth state from the login response directly — avoids a follow-up /auth/me that
    // races the just-set cross-site (samesite=none) cookie and would bounce back to /login.
    setUser(r.data.user);
    setAccess(r.data.access);
    setLoading(false);
    return r.data;
  };

  const registerPassword = async (email, password, name, betaCode) => {
    const r = await api.post("/auth/register", { email, password, name, beta_code: betaCode || null });
    setUser(r.data.user);
    setAccess(r.data.access);
    setLoading(false);
    return r.data;
  };

  // Seed auth state from an auth response payload ({user, access}) — used by the Google
  // OAuth callback so it lands cleanly without a follow-up /auth/me that races the cookie.
  const applySession = useCallback((data) => {
    setUser(data?.user || null);
    setAccess(data?.access || null);
    setLoading(false);
  }, []);

  const redeemBeta = async (code) => {
    const r = await api.post("/billing/redeem-beta", { code });
    await refresh();
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout request failed", e); }
    setUser(null);
    setAccess(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, access, loading, refresh, loginPassword, registerPassword, applySession, redeemBeta, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
